<template>
  <div class="folder_tag_list_container">
    <template v-if="folder">
      <OrganizationTag
        v-for="tag in folder.tags"
        :key="`folder-tag-${folder.id}-${tag.id}`"
        :tag="tag"
        :closable="true"
        @close="removeTag(tag)"
      />
    </template>
    <BaseMenu v-model="isPopoverOpen" offset-y :close-on-content-click="false">
      <template #activator="{ on, attrs }">
        <div id="add-menu" class="add_tag_btn" v-bind="attrs" v-on="on">
          <BaseIcon icon="$mdiPlus" x-small />
          {{ $t("folder_tag_list.add") }}
        </div>
      </template>
      <template #content>
        <BaseCard class="folder_tag_popover">
          <h3>{{ $t("folder_tag_list.menu_title") }}</h3>
          <div class="content">
            <BaseTextField
              ref="searchField"
              v-model="tagSearch"
              :placeholder="$t('utils.search')"
              prepend-inner-icon="$mdiMagnify"
              hide-details
              clearable
            />
            <v-list class="list">
              <v-list-item
                v-for="tag in filteredTags"
                :key="tag.id"
                @click="addTag(tag)"
              >
                <OrganizationTag :tag="tag" />
              </v-list-item>
            </v-list>
          </div>
          <template v-if="isManager">
            <v-divider />
            <v-card-actions>
              <BaseButton
                text
                type="primary"
                :to="{
                  name: 'ORGANIZATION_SETTINGS',
                  params: { organizationId: $route.params.organizationId },
                }"
              >
                {{ $t("folder_tag_list.manage_tags") }}
              </BaseButton>
            </v-card-actions>
          </template>
        </BaseCard>
      </template>
    </BaseMenu>
  </div>
</template>

<script>
import OrganizationTag from "@/modules/Admin/Components/OrganizationTag.vue";
import BaseMenu from "@/core/Components/UI/Menu/BaseMenu.vue";
import BaseCard from "@/core/Components/UI/Card/BaseCard.vue";
import BaseTextField from "@/core/Components/UI/Form/TextField/BaseTextField.vue";
import BaseIcon from "@/core/Components/UI/Icon/BaseIcon.vue";
import BaseButton from "@/core/Components/UI/Button/BaseButton.vue";
import { mapState } from "vuex";
import FolderApi from "@/modules/Folder/Services/folder.api.js";
import TagApi from "@/modules/Folder/Services/tag.api.js";

export default {
  name: "FolderTagList",
  components: {
    OrganizationTag,
    BaseIcon,
    BaseButton,
    BaseMenu,
    BaseCard,
    BaseTextField,
  },
  props: {
    folderId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      folder: null,
      isPopoverOpen: false,
      availableTags: [],
      tagSearch: "",
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.account.user,
    }),
    isManager() {
      return this.user?.isManager(this.$route.params.organizationId) ?? false;
    },
    filteredTags() {
      const search = this.tagSearch?.toLowerCase() || undefined;
      return this.availableTags.filter((tag) => {
        if (this.folder) {
          if (this.folder.tags.find((t) => t.id === tag.id) != undefined) {
            return false;
          }
        }

        if (search) {
          return tag.label.toLowerCase().includes(search);
        }

        return true;
      });
    },
  },
  watch: {
    isPopoverOpen(value) {
      if (value) {
        this.fetchTags();
        setTimeout(() => {
          this.$refs.searchField?.$el?.querySelector("input")?.focus();
        }, 100);
      }
    },
  },
  created() {
    this.fetchFolder();
  },
  methods: {
    async fetchFolder() {
      try {
        const res = await FolderApi.readFolder(
          this.$route.params.organizationId,
          this.folderId
        );
        this.folder = res.data;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async fetchTags() {
      try {
        const res = await TagApi.getOrganizationTagList(
          this.$route.params.organizationId
        );
        this.availableTags = res.data.results;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async addTag(tag) {
      try {
        await FolderApi.addFolderTag(
          this.$route.params.organizationId,
          this.folder.id,
          tag.id
        );
        await this.fetchFolder();
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async removeTag(tag) {
      try {
        await FolderApi.removeFolderTag(
          this.$route.params.organizationId,
          this.folder.id,
          tag.id
        );
        await this.fetchFolder();
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.folder_tag_list_container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.add_tag_btn {
  height: 26px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px dashed #b0aec0;
  border-radius: 6px;
  padding: 0 8px;
  cursor: pointer;
  color: #999;
  font-size: 10pt;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: #6b6b8e;
    color: #555;
  }
}

.folder_tag_popover {
  padding: 12px 16px;

  .content {
    margin-top: 12px;
    margin-bottom: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .list {
    max-height: 220px;
    overflow-y: auto;
  }
}
</style>
